import {
  useGetApiV1ExamExamitemidApproveEditor,
  useGetApiV1ExamExamitemidExamList,
} from '@/shared/api/generated';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Prova que utiliza esta questão */
export interface ExamHistoryItem {
  readonly id: string;
  readonly title: string;
  readonly discipline: string;
  readonly examType: string;
  readonly isActive: boolean;
  readonly creationDate: string;
  readonly lastChangeDate: string;
}

/** Status de aprovação da questão */
export interface QuestionApprovalStatus {
  readonly status: number;
  readonly statusDescription: string;
  readonly statusJustification: string;
  readonly isApprover: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook para buscar o histórico de uma questão da Biblioteca de Questões.
 *
 * Combina duas fontes:
 * - `useGetApiV1ExamExamitemidExamList` → provas que utilizam a questão
 * - `useGetApiV1ExamExamitemidApproveEditor` → status de aprovação atual
 *
 * @param questionId - ID da questão (string) ou null quando nenhuma está selecionada
 * @returns Lista de provas, status de aprovação e estado de loading/error
 *
 * @example
 * ```tsx
 * const { exams, approval, isLoading } = useQuestionHistory(selectedId);
 * ```
 */
export function useQuestionHistory(questionId: string | null) {
  const numericId = questionId ? Number(questionId) : 0;
  const enabled = !Number.isNaN(numericId) && numericId > 0;

  const examListQuery = useGetApiV1ExamExamitemidExamList(numericId, undefined, {
    query: { enabled },
  });

  const approvalQuery = useGetApiV1ExamExamitemidApproveEditor(numericId, {
    query: { enabled },
  });

  const exams: ExamHistoryItem[] = (examListQuery.data?.data ?? []).map(e => ({
    id: String(e.id ?? ''),
    title: e.title ?? '',
    discipline: e.disciplineText ?? '',
    examType: e.examTypeText ?? '',
    isActive: e.active ?? false,
    creationDate: e.creationDate ?? '',
    lastChangeDate: e.lastChangeDate ?? '',
  }));

  const approval: QuestionApprovalStatus | null = approvalQuery.data
    ? {
        status: approvalQuery.data.status ?? 0,
        statusDescription: approvalQuery.data.statusDescription ?? '',
        statusJustification: approvalQuery.data.statusJustification ?? '',
        isApprover: approvalQuery.data.isApprover ?? false,
      }
    : null;

  return {
    exams,
    approval,
    totalExams: examListQuery.data?.totalCount ?? 0,
    isLoading: examListQuery.isPending || approvalQuery.isPending,
    isError: examListQuery.isError || approvalQuery.isError,
  } as const;
}
